import { useQuickReplies } from '../../features/quick-replies/viewmodels/useQuickReplies';
import { quickReplyMedia } from '../../features/quick-replies/models/quickReplyMedia';
import { getQuickReplies } from '../../features/quick-replies/models/quickReplyStore';
import SettingsHeaderActions from '../../features/settings/SettingsHeaderActions';
import SettingsSwitchStrip from '../../features/settings/SettingsSwitchStrip';
import SettingsLoading from '../../features/settings/SettingsLoading';
import ScrollEdgeFrame from '../shared/ScrollEdgeFrame';
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Empty, Menu, Space, Tag, Typography } from "antd";
import { Select } from "../shared/OptionWidthControl";
import { useAccess } from "../../features/access/context/AccessProvider";
import { fetchCanonicalSnippetAttachmentBlob, fetchActionBindings, fetchCanonicalChannelAccounts, fetchMessagingChannelSettings, saveActionBinding, saveMessagingChannelSettings } from "../messaging/canonicalMessagingApi";
import { snippetAttachments } from "../messaging/canonicalSnippet";
import { ct, WaText } from "../qv4/MessageParts";
import { useAppearance } from "../contexts/AppearanceContext";

const ACTIONS = { quote_b2c: "Customer quote — B2C", quote_b2b: "Customer quote — B2B", parts_unavailable: "Parts unavailable" };

export function SnippetMediaPreview({ snippet, channelId, review = false }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let active = true;
    const ownedUrls = [];
    setItems([]);
    Promise.all(snippetAttachments(snippet).map(async attachment => {
      try {
        const media = review ? { blob: await fetchCanonicalSnippetAttachmentBlob(snippet.id, attachment.id, channelId, { review: true }) } : await quickReplyMedia.get(snippet.id, attachment.id, channelId);
        const { blob } = media;
        if (!active) return null;
        const url = review ? URL.createObjectURL(blob) : media.url;
        if (review) ownedUrls.push(url);
        if (!active) return null;
        return { attachment, url, type: blob.type || attachment.mime_type || "", error: false };
      } catch { return { attachment, error: true }; }
    })).then(result => { if (active) setItems(result.filter(Boolean)); });
    return () => { active = false; ownedUrls.forEach(url => URL.revokeObjectURL(url)); };
  }, [snippet, channelId, review]);
  return items.map(({ attachment, url, type, error }) => <div key={attachment.id}>
    <Typography.Text>{attachment.file_name || "Attachment"}</Typography.Text>
    {error ? <Alert type="warning" message="Media preview unavailable" />
      : type.startsWith("image/") || attachment.kind === "image" ? <img src={url} alt={attachment.file_name || "Quick reply image"} style={{ display: "block", maxWidth: "100%", maxHeight: 180 }} />
        : type.startsWith("audio/") || attachment.kind === "audio" ? <audio controls src={url} />
          : type.startsWith("video/") || attachment.kind === "video" ? <video controls src={url} style={{ maxWidth: "100%", maxHeight: 180 }} />
            : <a href={url} download={attachment.file_name || "attachment"}>Preview / download attachment</a>}
  </div>);
}

// The message as the customer or supplier receives it: an outgoing bubble on
// the chat wallpaper, in the channel's colours.
export function DefaultMessagePreview({ label, snippet, channelId, audience, unsaved, missing }) {
  const { isDark } = useAppearance();
  const theme = ct(isDark, audience === "supplier" ? "supplier" : "customer");
  return <div className="messaging-defaults-preview" data-testid="default-message-preview">
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
      <Typography.Text strong>Preview</Typography.Text>
      <Typography.Text type="secondary">{label}</Typography.Text>
      {unsaved && <Tag color="warning" style={{ margin: 0 }}>Not saved yet</Tag>}
    </div>
    <div style={{ background: theme.bg, borderRadius: 10, padding: 14, minHeight: 160 }}>
      {snippet ? <div style={{ marginLeft: "auto", width: "fit-content", maxWidth: "92%", padding: "6px 9px", borderRadius: "8px 0 8px 8px",
        background: theme.bubbleOut, color: theme.text, boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)", fontSize: 13, lineHeight: "19px", overflowWrap: "anywhere" }}>
        <Space direction="vertical" size={6} style={{ width: "100%" }}><SnippetMediaPreview snippet={snippet} channelId={channelId} /></Space>
        {snippet.body && <div style={{ whiteSpace: "pre-wrap" }}><WaText text={snippet.body} /></div>}
      </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={missing ? "The chosen message no longer exists. Choose again." : "No message chosen"} />}
    </div>
  </div>;
}

function BindingRow({ row, snippets, channelId, channelLabel, refresh, active, onActivate, onDraftChange }) {
  const [selected, setSelected] = useState(row.snippet_id);
  useEffect(() => { onDraftChange(row.action, selected); }, [row.action, selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const flight = useRef(false);
  useEffect(() => { setSelected(row.snippet_id); }, [row.snippet_id, row.version]);
  const snippet = snippets.find(item => item.id === selected);
  const missing = selected && !snippet;
  const save = async () => {
    if (flight.current || missing) return;
    flight.current = true; setSaving(true); setError("");
    try { await saveActionBinding(channelId, row.action, selected || null, row.version); if (!await refresh()) { setError("Saved, but reloading settings failed. Reload before editing again."); return false; } return true; }
    catch (failure) {
      if (failure.code === "MESSAGING_BINDING_VERSION_CONFLICT") { const reloaded = await refresh(); setError(reloaded ? "This binding changed. Latest settings reloaded; review and choose again." : "This binding changed. Reload settings before saving again."); }
      else setError(failure.code === "MESSAGING_SNIPPET_NOT_AVAILABLE" ? "This quick reply is no longer available. Choose another message." : failure.message);
      return false;
    } finally { flight.current = false; setSaving(false); }
  };
  return <Card size="small" title={ACTIONS[row.action] || row.action} onClick={() => onActivate(row.action)} onFocusCapture={() => onActivate(row.action)}
    aria-current={active || undefined} style={{ cursor: "pointer", borderColor: active ? "var(--app-color-accent)" : undefined, boxShadow: active ? "0 0 0 1px var(--app-color-accent)" : undefined }}>
    <Space direction="vertical" style={{ width: "100%" }}>
      <Typography.Text>Current: {row.snippet_id ? snippets.find(item => item.id === row.snippet_id)?.title || "Missing — choose again" : "Not set"}</Typography.Text>
      <Select aria-label={`Default ${row.action}`} showSearch optionFilterProp="label" allowClear value={selected || undefined} placeholder="Not set" disabled={saving}
        onChange={value => { setSelected(value || null); onActivate(row.action); }} style={{ width: "100%" }} options={snippets.map(item => ({ value: item.id, label: item.title || "Quick reply" }))} />
      {missing && <Alert type="warning" message="Missing — choose again" />}
      {error && <Alert type="error" message={error} />}
      <SettingsHeaderActions dirty={selected !== row.snippet_id} label={channelLabel}><Button onClick={save} loading={saving} disabled={missing || selected === row.snippet_id}>Save {ACTIONS[row.action] || row.action}</Button></SettingsHeaderActions>
    </Space>
  </Card>;
}

function ChannelPanel({ channel, ownership, external = false }) {
  const [data, setData] = useState(null);
  const { items: snippets } = useQuickReplies(channel.id,!ownership);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const revision = useRef(0);
  const flight = useRef(false);
  const [activeAction, setActiveAction] = useState(null);
  const [drafts, setDrafts] = useState({});
  const onDraftChange = useRef((action, snippetId) => setDrafts(current => current[action] === snippetId ? current : { ...current, [action]: snippetId })).current;
  const refresh = async () => {
    const run = ++revision.current;
    try {
      const [result] = ownership ? [await fetchMessagingChannelSettings(channel.id), []]
        : await Promise.all([fetchActionBindings(channel.id, null, true), getQuickReplies(channel.id)]);
      if (run !== revision.current) return;
      setData(result);
      return true;
    } catch (failure) { if (run === revision.current) setError(failure.message); return false; }
    finally { if (run === revision.current) setLoading(false); }
  };
  useEffect(() => { refresh(); return () => { revision.current += 1; }; }, [channel.id, ownership]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async selected => {
    if (!data || flight.current) return false;
    flight.current = true; setSaving(true); setError(""); setNotice("");
    try { await saveMessagingChannelSettings(channel.id, selected, data.version); const refreshed = await refresh(); setNotice(refreshed ? "Saved." : "Saved, but reloading settings failed. Reload before editing again."); return refreshed; }
    catch (failure) {
      if (failure.code === "MESSAGING_SETTINGS_VERSION_CONFLICT") { const reloaded = await refresh(); setNotice(reloaded ? "This setting changed. Latest settings reloaded; review before saving again." : "This setting changed. Reload settings before saving again."); }
      else setError(failure.message);
      return false;
    } finally { flight.current = false; setSaving(false); }
  };
  return <Card title={external ? (ownership ? 'Ownership on send' : 'Default messages') : (channel.display_name || channel.name || channel.key || channel.channel_account_key)}>
    {error && <Alert type="error" message={error} action={<Button onClick={() => { setError(""); refresh(); }}>Reload</Button>} />}
    {notice && <Alert type="info" message={notice} />}
    {loading ? <SettingsLoading /> : data && (ownership ? <Space direction="vertical">
      <SettingsSwitchStrip label="Take over ownership on send" description="When off, sending a message doesn't make you the chat's owner." checked={data.takeover_on_send} disabled={saving} onSave={save} confirm={{ title: 'Change ownership on send?', content: 'This saves now and changes whether future sends take ownership of a chat. Existing ownership is unchanged.' }} />
    </Space> : (() => {
      const activeRow = data.find(row => row.action === activeAction) || data[0];
      const draft = activeRow && (activeRow.action in drafts ? drafts[activeRow.action] : activeRow.snippet_id);
      const previewSnippet = snippets.find(item => item.id === draft);
      return <div className="messaging-defaults-grid">
        <Space direction="vertical" style={{ width: "100%" }}>{data.map(row => <BindingRow key={row.action} row={row} snippets={snippets} channelId={channel.id}
          channelLabel={channel.display_name || channel.name || channel.audience} refresh={refresh}
          active={row === activeRow} onActivate={setActiveAction} onDraftChange={onDraftChange} />)}</Space>
        {activeRow && <DefaultMessagePreview label={ACTIONS[activeRow.action] || activeRow.action} snippet={previewSnippet} channelId={channel.id} audience={channel.audience}
          unsaved={(draft || null) !== (activeRow.snippet_id || null)} missing={Boolean(draft && !previewSnippet)} />}
      </div>;
    })())}
  </Card>;
}

function Content({ ownership }) {
  const [channels, setChannels] = useState([]);
  const [error, setError] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { let active = true; fetchCanonicalChannelAccounts().then(items => { if (active) { setChannels(items); setSelectedChannel(items[0]?.id); } }).catch(failure => { if (active) setError(failure.message); }).finally(() => { if (active) setLoading(false); }); return () => { active = false; }; }, []);
  if (loading) return <SettingsLoading />;
  return <div className="messaging-defaults-workspace">
    <nav className="messaging-defaults-channels" aria-label="WhatsApp channel"><Menu selectedKeys={[selectedChannel]} onClick={({ key }) => setSelectedChannel(key)} items={channels.map(channel => ({ key: channel.id, label: channel.display_name || channel.name || 'Number' }))} /></nav>
    <ScrollEdgeFrame style={{ flex: 1 }}><div data-scroll-edge-viewport className="messaging-defaults-content">{error && <Alert type="error" message={error} />}{channels.map(channel => <div key={`${channel.id}:${ownership}`} hidden={channel.id !== selectedChannel}><ChannelPanel channel={channel} ownership={ownership} /></div>)}</div></ScrollEdgeFrame>
  </div>;
}

export function MessagingDefaultsPanel({ ownership = false, channel = null }) {
  const { can } = useAccess();
  return can("settings.chat.manage") ? (channel ? <ChannelPanel key={`${channel.id}:${ownership}`} channel={channel} ownership={ownership} external/> : <Content ownership={ownership} />) : <Alert type="warning" message="You do not have permission to manage messaging settings." />;
}
